import React, { useMemo, useState, useRef } from 'react'
import registry from '../component-registry.json'
import type { ComponentEntry, ComponentRegistry } from '../types'
import { TierSection } from './TierSection'
import { GroupSection } from './GroupSection'
import { tier1Previews } from '../previews/Tier1Previews'
import { tier2Previews, FbUITabsPreview } from '../previews/Tier2Previews'
import { tier3Mocks } from '../mocks'
import { componentGroups, excludedPaths } from './component-groups.config'
import { Search, X } from 'lucide-react'

const data = registry as ComponentRegistry

// Path-based overrides take precedence over name-based lookup — resolves
// name collisions (e.g. two components both named "Tabs").
const pathPreviews: Record<string, () => React.ReactNode> = {
  'components/FbUI/Tabs.tsx': FbUITabsPreview,
  'components/Tabs': tier3Mocks['Tabs'],
}
const allPreviews = { ...tier1Previews, ...tier2Previews, ...tier3Mocks }

function getPreviewer(entry: ComponentEntry): React.ReactNode | undefined {
  const PreviewFn = pathPreviews[entry.path] ?? allPreviews[entry.name]
  return PreviewFn ? <PreviewFn /> : undefined
}

type ViewMode = 'groups' | 'tiers'

const tierDefinitions = [
  {
    tier: 1 as const,
    label: 'Tier 1 · Primitives',
    folder: 'components/UI/',
    description:
      'shadcn/Radix UI wrappers. No business logic, minimal styling. Classified by Radix import or cva+Slot pattern.',
    color: 'border-success-bg bg-success-bg text-success',
    countColor: 'text-success',
    folderColor: 'bg-success text-white',
  },
  {
    tier: 2 as const,
    label: 'Tier 2 · Extended',
    folder: 'components/FbUI/',
    description:
      'Our branded extensions of primitives. Light composition, no API dependencies, broadly reusable.',
    color: 'border-amber-200 bg-amber-50 text-amber-900',
    countColor: 'text-amber-700',
    folderColor: 'bg-amber-700 text-white',
  },
  {
    tier: 3 as const,
    label: 'Tier 3 · Composites',
    folder: 'components/',
    description:
      'Domain features with state, business logic, or API calls. Everything not in UI/ or FbUI/ lands here.',
    color: 'border-orange-200 bg-orange-50 text-orange-800',
    countColor: 'text-orange-800',
    folderColor: 'bg-orange-700 text-white',
  },
]

export function ComponentLibraryPage(): React.ReactElement {
  const [view, setView] = useState<ViewMode>('groups')
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  const allComponents = useMemo(
    () => data.components.filter((c) => !excludedPaths.has(c.path)),
    []
  )

  const q = query.trim().toLowerCase()
  const filteredComponents = useMemo(() => {
    if (!q) return allComponents
    return allComponents.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.path.toLowerCase().includes(q) ||
        c.exportedNames.some((n) => n.toLowerCase().includes(q))
    )
  }, [allComponents, q])

  const tier1 = useMemo(
    () => filteredComponents.filter((c) => c.tier === 1),
    [filteredComponents]
  )
  const tier2 = useMemo(
    () => filteredComponents.filter((c) => c.tier === 2),
    [filteredComponents]
  )
  const tier3 = useMemo(
    () => filteredComponents.filter((c) => c.tier === 3),
    [filteredComponents]
  )
  const tierCounts = { 1: tier1.length, 2: tier2.length, 3: tier3.length }

  const componentsByPath = useMemo(
    () =>
      new Map<string, ComponentEntry>(
        filteredComponents.map((c) => [c.path, c])
      ),
    [filteredComponents]
  )

  const generatedDate = new Date(data.generatedAt).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-fg">Component Library</h1>
          <p className="text-fg-secondary text-sm mt-1">
            {q
              ? `${filteredComponents.length} of ${allComponents.length} components`
              : `${allComponents.length} components · Last analyzed ${generatedDate}`}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-muted pointer-events-none"
            />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search components..."
              className="pl-8 pr-7 py-1.5 text-sm border border-line rounded-md bg-surface focus:outline-none focus:ring-1 focus:ring-brand w-52"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-muted hover:text-fg"
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* View toggle */}
          <div className="flex rounded-md border border-line overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => setView('groups')}
              className={`px-4 py-2 font-medium transition-colors ${
                view === 'groups'
                  ? 'bg-brand text-white'
                  : 'bg-surface text-fg-secondary hover:bg-surface-hover'
              }`}
            >
              By Group
            </button>
            <button
              type="button"
              onClick={() => setView('tiers')}
              className={`px-4 py-2 font-medium transition-colors border-l border-line ${
                view === 'tiers'
                  ? 'bg-brand text-white'
                  : 'bg-surface text-fg-secondary hover:bg-surface-hover'
              }`}
            >
              By Tier
            </button>
          </div>
        </div>
      </div>

      {/* Tier legend */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
        {tierDefinitions.map(
          ({
            tier,
            label,
            folder,
            description,
            color,
            countColor,
            folderColor,
          }) => (
            <div
              key={tier}
              className={`border rounded-lg px-4 py-3 flex flex-col gap-2 ${color}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-sm">{label}</span>
                <span
                  className={`text-xs font-mono font-semibold ${countColor}`}
                >
                  {tierCounts[tier]}
                </span>
              </div>
              <code
                className={`self-start text-xxs px-2 py-0.5 rounded font-mono ${folderColor}`}
              >
                {folder}
              </code>
              <p className="text-xs leading-relaxed opacity-80">
                {description}
              </p>
            </div>
          )
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-4">
        {view === 'groups' ? (
          componentGroups.map((group) => {
            const groupComponents = group.componentPaths
              .map((p) => componentsByPath.get(p))
              .filter((c): c is ComponentEntry => c !== undefined)
            const resolvedClusters = (group.clusters ?? [])
              .map((cluster) => ({
                label: cluster.label,
                components: cluster.componentPaths
                  .map((p) => componentsByPath.get(p))
                  .filter((c): c is ComponentEntry => c !== undefined),
              }))
              .filter((c) => c.components.length > 0)
            return (
              <GroupSection
                key={group.label}
                group={group}
                components={groupComponents}
                clusters={resolvedClusters}
                getPreviewer={getPreviewer}
              />
            )
          })
        ) : (
          <>
            <TierSection
              tier={1}
              components={tier1}
              getPreviewer={getPreviewer}
            />
            <TierSection
              tier={2}
              components={tier2}
              getPreviewer={getPreviewer}
            />
            <TierSection
              tier={3}
              components={tier3}
              getPreviewer={getPreviewer}
            />
          </>
        )}
      </div>
    </div>
  )
}
